import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FilePdf, EnvelopeSimple, Trash, Plus, GasPump, CalendarX, CurrencyInr, FloppyDisk, CheckCircle, XCircle, UserPlus, FileArrowDown, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const today = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EXP_STATUS = { submitted: "text-amber-700 bg-amber-50 border-amber-200", approved: "text-green-700 bg-green-50 border-green-200", rejected: "text-red-700 bg-red-50 border-red-200" };

export default function Employees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initialTab = (() => { const t = sp.get("tab"); return t === "visits" ? "visits" : t === "profile" ? "profile" : "salary"; })();
  const manager = user?.role === "owner" || user?.role === "accountant";
  const [employees, setEmployees] = useState([]);
  const [uid, setUid] = useState(manager ? (sp.get("uid") || "") : user.id);
  const [month, setMonth] = useState(thisMonth());

  const exportPayroll = async (fmt) => {
    try {
      const { data } = await api.get(`/employees/payroll-export?month=${month}&fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      if (fmt === "pdf") { window.open(url, "_blank"); }
      else { const a = document.createElement("a"); a.href = url; a.download = `Payroll-${month}.csv`; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (manager) api.get("/employees").then((r) => { setEmployees(r.data); if (!uid && r.data[0]) setUid(r.data[0].id); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [manager]); // eslint-disable-line

  const selected = employees.find((e) => e.id === uid);

  return (
    <div className="space-y-6" data-testid="employees-page">
      <PageHeader overline="People & Payroll" title="Employee Module" subtitle="Salary & attendance, site visits & fuel charges, tour expenses and advances" />

      <Card className="p-4 border-border flex flex-wrap items-end gap-4">
        {manager && (
          <div className="space-y-1.5">
            <Label className="text-xs">Employee</Label>
            <Select value={uid} onValueChange={setUid}>
              <SelectTrigger className="w-64 h-9" data-testid="employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {(e.role || "").replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-9" data-testid="month-select" />
        </div>
        {manager && <Button variant="outline" className="h-9 rounded-full" onClick={() => navigate("/app/team")} data-testid="new-employee-btn"><UserPlus size={15} className="mr-1" /> New Employee</Button>}
        {manager && <Button variant="outline" className="h-9 rounded-full" onClick={() => exportPayroll("csv")} data-testid="payroll-csv-btn"><FileArrowDown size={15} className="mr-1" /> Payroll CSV</Button>}
        {manager && <Button variant="outline" className="h-9 rounded-full" onClick={() => exportPayroll("pdf")} data-testid="payroll-pdf-btn"><FilePdf size={15} className="mr-1" /> Payroll PDF</Button>}
        {manager && selected && <BasicSalaryEditor emp={selected} onSaved={() => api.get("/employees").then((r) => setEmployees(r.data))} />}
      </Card>

      {!uid ? (
        <div className="text-sm text-muted-foreground">Select an employee to begin.</div>
      ) : (
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="salary" data-testid="tab-salary">Salary</TabsTrigger>
            <TabsTrigger value="visits" data-testid="tab-visits">Site Visits & Fuel</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
            {manager && <TabsTrigger value="advances" data-testid="tab-advances">Advances</TabsTrigger>}
            {manager && <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile" className="mt-4"><ProfileTab uid={uid} manager={manager} /></TabsContent>

          <TabsContent value="salary" className="mt-4"><SalaryTab uid={uid} month={month} manager={manager} /></TabsContent>
          <TabsContent value="visits" className="mt-4"><VisitsTab uid={uid} month={month} /></TabsContent>
          <TabsContent value="expenses" className="mt-4"><ExpensesTab uid={uid} month={month} manager={manager} /></TabsContent>
          {manager && <TabsContent value="advances" className="mt-4"><AdvancesTab uid={uid} month={month} manager={manager} /></TabsContent>}
          {manager && <TabsContent value="attendance" className="mt-4"><AttendanceTab uid={uid} month={month} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
}

function BasicSalaryEditor({ emp, onSaved }) {
  const [basic, setBasic] = useState(emp.basic_salary || "");
  const [wd, setWd] = useState(emp.working_days || "");
  useEffect(() => { setBasic(emp.basic_salary || ""); setWd(emp.working_days || ""); }, [emp.id]); // eslint-disable-line
  const save = async () => {
    try { await api.put(`/employees/${emp.id}`, { basic_salary: Number(basic) || 0, working_days: Number(wd) || 0 }); toast.success("Saved"); onSaved && onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-end gap-2 ml-auto">
      <div className="space-y-1.5"><Label className="text-xs">Basic (₹/month)</Label><Input type="number" value={basic} onChange={(e) => setBasic(e.target.value)} className="w-32 h-9" data-testid="basic-salary-input" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Working days</Label><Input type="number" value={wd} onChange={(e) => setWd(e.target.value)} className="w-28 h-9" placeholder="default" data-testid="working-days-input" /></div>
      <Button onClick={save} variant="outline" className="h-9 rounded-full" data-testid="save-basic-btn"><FloppyDisk size={15} className="mr-1" /> Save</Button>
    </div>
  );
}

function SalaryTab({ uid, month, manager }) {
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState(false);
  const load = useCallback(() => api.get(`/employees/${uid}/salary?month=${month}&include_extras=${extras}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [uid, month, extras]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    try {
      const { data: blob } = await api.get(`/employees/${uid}/salary-slip.pdf?month=${month}&include_extras=${extras}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const email = async () => {
    try { const { data: r } = await api.post(`/employees/${uid}/email-salary-slip?month=${month}`); toast.success(`Emailed to ${r.recipient}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <div className="text-sm text-muted-foreground animate-pulse">Loading salary…</div>;
  const rows = [["Basic (monthly)", inr(data.basic)], ["Working days", data.working_days], ["Per-day rate", inr(data.per_day)], ["Absent days", data.absent_days], ["Absence deduction", "- " + inr(data.deduction)]];
  return (
    <Card className="p-5 border-border space-y-4" data-testid="salary-tab">
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-dashed py-1 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>)}
      </div>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="checkbox" checked={extras} onChange={(e) => setExtras(e.target.checked)} data-testid="salary-extras-toggle" />
        Include approved fuel + expenses − advances as a combined take-home
      </label>
      <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
        <span className="font-display font-semibold text-primary flex items-center gap-1.5"><CurrencyInr size={18} /> Net Salary Payable</span>
        <span className="font-display font-black text-2xl text-primary" data-testid="salary-net">{inr(data.net)}</span>
      </div>
      {data.include_extras && (
        <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 space-y-1" data-testid="salary-takehome-box">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Fuel charges</span><span>+ {inr(data.fuel)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Approved expenses</span><span>+ {inr(data.expenses)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Advance recovery {data.advance_settled ? "(settled)" : "(auto)"}</span><span data-testid="salary-adv-recovery">- {inr(data.advance_recovery)}</span></div>
          {data.advance_outstanding > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Advance balance carried forward</span><span className="text-amber-700" data-testid="salary-adv-balance">{inr(data.advance_outstanding)}</span></div>}
          <div className="flex items-center justify-between pt-1 border-t mt-1"><span className="font-display font-semibold text-primary">Net Take-home</span><span className="font-display font-black text-2xl text-primary" data-testid="salary-takehome">{inr(data.take_home)}</span></div>
        </div>
      )}
      {data.absences?.length > 0 && (
        <div className="text-xs">
          <div className="font-semibold mb-1">Absence details</div>
          {data.absences.map((a, i) => <div key={i} className="flex gap-3 text-muted-foreground"><span className="w-24">{a.date}</span><span>{a.reason || "—"}</span></div>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={download} variant="outline" className="rounded-full" data-testid="salary-download-btn"><FilePdf size={16} className="mr-1" /> Download slip (PDF)</Button>
        {manager && <Button onClick={email} variant="outline" className="rounded-full" data-testid="salary-email-btn"><EnvelopeSimple size={16} className="mr-1" /> Email to employee</Button>}
      </div>
      <p className="text-[11px] text-muted-foreground">Fuel charges, expenses and advances are tracked in their own tabs and are not part of this slip.</p>
    </Card>
  );
}

function VisitsTab({ uid, month }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ date: today(), client: "", from_address: "", to_address: "", purpose: "", photo: "", photo_lat: null, photo_lng: null });
  const photoRef = useRef();
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/employees/${uid}/upload`, fd);
      let coords = {};
      await new Promise((res) => { if (!navigator.geolocation) return res(); navigator.geolocation.getCurrentPosition((p) => { coords = { photo_lat: p.coords.latitude, photo_lng: p.coords.longitude }; res(); }, () => res(), { timeout: 4000 }); });
      setF((x) => ({ ...x, photo: data.path, ...coords }));
      toast.success("Photo attached" + (coords.photo_lat ? " (geo-tagged)" : ""));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  const load = useCallback(() => api.get(`/employees/${uid}/site-visits?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [uid, month]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.date) return toast.error("Pick a date");
    if (!f.to_address) return toast.error("Enter the destination (To) address");
    try { await api.post(`/employees/${uid}/site-visits`, f); setF({ date: today(), client: "", from_address: "", to_address: "", purpose: "", photo: "", photo_lat: null, photo_lng: null }); load(); toast.success("Visit added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/employees/site-visits/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!data) return <div className="text-sm text-muted-foreground animate-pulse">Loading visits…</div>;
  return (
    <div className="space-y-4" data-testid="visits-tab">
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4 border-border"><div className="text-xs text-muted-foreground">Visits this month</div><div className="font-display font-black text-2xl text-primary">{data.visits.length}</div></Card>
        <Card className="p-4 border-border"><div className="text-xs text-muted-foreground">Total distance (round-trip)</div><div className="font-display font-black text-2xl text-primary">{data.total_km} km</div></Card>
        <Card className="p-4 border-border bg-accent/5"><div className="text-xs text-muted-foreground flex items-center gap-1"><GasPump size={13} /> Fuel charges @ ₹{data.fuel_rate}/km</div><div className="font-display font-black text-2xl text-primary" data-testid="visits-total-fuel">{inr(data.total_fuel)}</div></Card>
      </div>

      <Card className="p-4 border-border space-y-3">
        <div className="text-sm font-semibold">Add a manual visit (non-job trip)</div>
        <div className="grid sm:grid-cols-5 gap-2">
          <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="Visit date" className="h-9 w-full" data-testid="visit-date" />
          <Input placeholder="Client / bank" value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} className="h-9" data-testid="visit-client" />
          <Input placeholder="From (blank = office)" value={f.from_address} onChange={(e) => setF({ ...f, from_address: e.target.value })} className="h-9" data-testid="visit-from" />
          <Input placeholder="To — site address" value={f.to_address} onChange={(e) => setF({ ...f, to_address: e.target.value })} className="h-9" data-testid="visit-to" />
          <Input placeholder="Purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className="h-9" data-testid="visit-purpose" />
        </div>
        <p className="text-[11px] text-muted-foreground">Distance is computed From → To (round-trip). Leave "From" blank to use the office default origin. Addresses are auto-geocoded.</p>
        <div className="flex items-center gap-2">
          <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} data-testid="visit-photo-input" />
          <Button type="button" variant="outline" className="h-8 rounded-full text-[11px]" onClick={() => photoRef.current?.click()} data-testid="visit-photo-btn"><Camera size={14} className="mr-1" /> {f.photo ? "Photo attached ✓" : "Attach site photo (proof)"}</Button>
          <Button onClick={add} variant="outline" className="rounded-full" data-testid="visit-add-btn"><Plus size={15} className="mr-1" /> Add visit</Button>
        </div>
      </Card>

      <Card className="border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Client</th><th className="text-left p-2">Purpose</th><th className="text-left p-2">Source</th><th className="text-right p-2">Round-trip</th><th className="text-right p-2">Fuel</th><th className="p-2"></th></tr></thead>
          <tbody>
            {data.visits.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No site visits this month.</td></tr>}
            {data.visits.map((v) => (
              <tr key={v.id} className="border-t" data-testid={`visit-row`}>
                <td className="p-2 whitespace-nowrap">{v.date}</td>
                <td className="p-2">{v.client}</td>
                <td className="p-2 text-muted-foreground">{v.purpose}{v.photo ? <> · <a href={`${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(v.photo)}`} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid="visit-photo-link">photo{v.photo_lat ? " 📍" : ""}</a></> : null}</td>
                <td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${v.source === "job" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{v.source}</span></td>
                <td className="p-2 text-right whitespace-nowrap">{v.distance_km != null ? `${v.distance_km} km` : "—"}</td>
                <td className="p-2 text-right font-medium">{inr(v.fuel_amount)}</td>
                <td className="p-2 text-right">{v.source === "manual" && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(v.id)} data-testid="visit-del"><Trash size={14} /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ExpensesTab({ uid, month, manager }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: today(), amount: "", category: "Travel", note: "" });
  const load = useCallback(() => api.get(`/employees/${uid}/expenses?month=${month}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [uid, month]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.amount) return toast.error("Enter an amount");
    try { await api.post(`/employees/${uid}/expenses`, { ...f, amount: Number(f.amount) }); setF({ date: today(), amount: "", category: "Travel", note: "" }); load(); toast.success("Expense submitted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { try { await api.put(`/employees/expenses/${id}`, { status }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/employees/expenses/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const total = rows.reduce((s, r) => s + (r.status !== "rejected" ? Number(r.amount) : 0), 0);
  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <Card className="p-4 border-border space-y-3">
        <div className="text-sm font-semibold">Submit an expense</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="Date" className="h-9 w-full" data-testid="exp-date" />
          <Input type="number" placeholder="Amount ₹" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-9" data-testid="exp-amount" />
          <Input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-9" data-testid="exp-category" />
          <Input placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-9" data-testid="exp-note" />
        </div>
        <Button onClick={add} variant="outline" className="rounded-full" data-testid="exp-add-btn"><Plus size={15} className="mr-1" /> Submit expense</Button>
      </Card>
      <Card className="border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Category</th><th className="text-left p-2">Note</th><th className="text-right p-2">Amount</th><th className="text-left p-2">Status</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No expenses this month.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid="exp-row">
                <td className="p-2 whitespace-nowrap">{r.date}</td><td className="p-2">{r.category}</td><td className="p-2 text-muted-foreground">{r.note}</td>
                <td className="p-2 text-right font-medium">{inr(r.amount)}</td>
                <td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${EXP_STATUS[r.status] || ""}`}>{r.status}</span></td>
                <td className="p-2 text-right whitespace-nowrap">
                  {manager && r.status === "submitted" && (<>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => setStatus(r.id, "approved")} data-testid="exp-approve"><CheckCircle size={15} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => setStatus(r.id, "rejected")} data-testid="exp-reject"><XCircle size={15} /></Button>
                  </>)}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)} data-testid="exp-del"><Trash size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end p-2 text-sm font-semibold border-t">Approved/pending total: {inr(total)}</div>
      </Card>
    </div>
  );
}

function AdvancesTab({ uid, month, manager }) {
  const [rows, setRows] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [f, setF] = useState({ date: today(), amount: "", installment: "", note: "" });
  const load = useCallback(() => {
    api.get(`/employees/${uid}/advances?month=${month}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get(`/employees/${uid}/advance-ledger?month=${month}`).then((r) => setLedger(r.data)).catch(() => setLedger(null));
  }, [uid, month]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.amount) return toast.error("Enter an amount");
    try {
      await api.post(`/employees/${uid}/advances`, { date: f.date, note: f.note, amount: Number(f.amount), installment: f.installment ? Number(f.installment) : null });
      setF({ date: today(), amount: "", installment: "", note: "" }); load(); toast.success("Advance recorded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/employees/advances/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const settle = async () => {
    try { const { data } = await api.post(`/employees/${uid}/advances/settle?month=${month}`); toast.success(`Recovered ${inr(data.recovery_total)} · balance ${inr(data.outstanding)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const total = rows.reduce((s, r) => s + Number(r.amount), 0);
  const L = ledger?.ledger;
  return (
    <div className="space-y-4" data-testid="advances-tab">
      <Card className="p-4 border-border space-y-3">
        <div className="text-sm font-semibold">Record an advance paid</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="Date" className="h-9 w-full" data-testid="adv-date" />
          <Input type="number" placeholder="Amount ₹" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-9" data-testid="adv-amount" />
          <Input type="number" placeholder="Installment ₹/mo (optional)" value={f.installment} onChange={(e) => setF({ ...f, installment: e.target.value })} className="h-9" data-testid="adv-installment" />
          <Input placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-9" data-testid="adv-note" />
        </div>
        <p className="text-[11px] text-muted-foreground">Leave installment blank to recover the full balance each month. Recovery is capped so take-home never goes negative — any shortfall carries to the next month automatically.</p>
        <Button onClick={add} variant="outline" className="rounded-full" data-testid="adv-add-btn"><Plus size={15} className="mr-1" /> Add advance</Button>
      </Card>

      {L && (
        <Card className="p-4 border-border space-y-3" data-testid="advance-ledger">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold">Outstanding advances ledger · {month}</div>
            {manager && month && <Button onClick={settle} size="sm" variant="outline" className="rounded-full" data-testid="adv-settle-btn"><CheckCircle size={15} className="mr-1" /> {ledger.settled ? "Re-settle this month" : "Record recovery for this month"}</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Note</th><th className="text-right p-2">Original</th><th className="text-right p-2">Recovered earlier</th><th className="text-right p-2">This month</th><th className="text-right p-2">Balance</th></tr></thead>
              <tbody>
                {L.rows.length === 0 && <tr><td colSpan={6} className="p-3 text-center text-muted-foreground">No advances on record.</td></tr>}
                {L.rows.map((r) => (
                  <tr key={r.id} className="border-t" data-testid="ledger-row">
                    <td className="p-2 whitespace-nowrap">{r.date}</td>
                    <td className="p-2 text-muted-foreground">{r.note || "—"}{r.installment > 0 ? ` · ₹${r.installment}/mo` : ""}</td>
                    <td className="p-2 text-right">{inr(r.amount)}</td>
                    <td className="p-2 text-right text-muted-foreground">{inr(r.recovered_before)}</td>
                    <td className="p-2 text-right font-medium text-green-700">{inr(r.this_month)}</td>
                    <td className="p-2 text-right font-medium text-amber-700">{inr(r.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm font-semibold border-t pt-2">
            <span>Recovered this month: <span className="text-green-700" data-testid="ledger-recovery">{inr(L.recovery_total)}</span></span>
            <span>Balance carried forward: <span className="text-amber-700" data-testid="ledger-outstanding">{inr(L.outstanding_after)}</span></span>
          </div>
        </Card>
      )}

      <Card className="border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Note</th><th className="text-right p-2">Amount</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No advances this month.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid="adv-row">
                <td className="p-2 whitespace-nowrap">{r.date}</td><td className="p-2 text-muted-foreground">{r.note}{r.installment ? ` · ₹${r.installment}/mo` : ""}</td>
                <td className="p-2 text-right font-medium">{inr(r.amount)}</td>
                <td className="p-2 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)} data-testid="adv-del"><Trash size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end p-2 text-sm font-semibold border-t">Advances added this month: {inr(total)}</div>
      </Card>
    </div>
  );
}

function AttendanceTab({ uid, month }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: today(), reason: "" });
  const load = useCallback(() => api.get(`/employees/${uid}/attendance?month=${month}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [uid, month]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.date) return toast.error("Pick a date");
    try { await api.post(`/employees/${uid}/attendance`, f); setF({ date: today(), reason: "" }); load(); toast.success("Absence marked"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/employees/attendance/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-4" data-testid="attendance-tab">
      <Card className="p-4 border-border space-y-3">
        <div className="text-sm font-semibold flex items-center gap-1.5"><CalendarX size={16} /> Mark an absent day</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="Date" className="h-9 w-full" data-testid="att-date" />
          <Input placeholder="Reason (leave / sick / etc.)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-9 sm:col-span-2" data-testid="att-reason" />
        </div>
        <Button onClick={add} variant="outline" className="rounded-full" data-testid="att-add-btn"><Plus size={15} className="mr-1" /> Mark absent</Button>
      </Card>
      <Card className="border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Absent date</th><th className="text-left p-2">Reason</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">No absences marked this month.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid="att-row">
                <td className="p-2 whitespace-nowrap">{r.date}</td><td className="p-2 text-muted-foreground">{r.reason || "—"}</td>
                <td className="p-2 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)} data-testid="att-del"><Trash size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end p-2 text-sm font-semibold border-t">Total absent days: {rows.length}</div>
      </Card>
    </div>
  );
}


function ProfileTab({ uid, manager }) {
  const [p, setP] = useState(null);
  const fileRef = useRef();
  const docRef = useRef();
  const load = useCallback(() => api.get(`/employees/${uid}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [uid]);
  useEffect(() => { load(); }, [load]);
  const save = async (patch) => { try { await api.put(`/employees/${uid}`, patch); setP((x) => ({ ...x, ...patch })); toast.success("Saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const upload = async (e, cb) => { const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { const { data } = await api.post(`/employees/${uid}/upload`, fd); cb(data); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { e.target.value = ""; } };
  if (!p) return <div className="text-sm text-muted-foreground animate-pulse">Loading profile…</div>;
  const img = (path) => `${api.defaults.baseURL}/gis/object?path=${encodeURIComponent(path)}`;
  const F = ({ k, label, type }) => (
    <div className="space-y-1"><Label className="text-[11px]">{label}</Label>
      {type === "date" ? (
        <DatePicker value={p[k] || ""} onChange={manager ? (v) => { if (v !== (p[k] || "")) save({ [k]: v }); } : () => {}} disabled={!manager} clearable placeholder="Select date" className="h-8 w-full" data-testid={`profile-${k}`} />
      ) : (
        <Input type={type || "text"} defaultValue={p[k] || ""} disabled={!manager} onBlur={manager ? (e) => { if (e.target.value !== (p[k] || "")) save({ [k]: e.target.value }); } : undefined} className="h-8 text-xs" data-testid={`profile-${k}`} />
      )}
    </div>
  );
  return (
    <Card className="p-5 border-border space-y-4" data-testid="profile-tab">
      <div className="flex items-center gap-4">
        {p.photo ? <img src={img(p.photo)} alt="" className="h-20 w-20 rounded-full object-cover border" data-testid="profile-photo" /> : <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center text-2xl font-display text-primary">{(p.name || "?")[0]}</div>}
        <div>
          <div className="font-display text-lg font-semibold text-primary">{p.name}</div>
          <div className="text-xs text-muted-foreground">{(p.role || "").replace("_", " ")} · {p.email}{p.phone ? ` · ${p.phone}` : ""}</div>
          {manager && <><input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, (d) => save({ photo: d.path }))} data-testid="profile-photo-input" /><Button size="sm" variant="outline" className="h-7 mt-1 rounded-full text-[11px]" onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn"><Camera size={13} className="mr-1" /> Change photo</Button></>}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <F k="join_date" label="Join date" type="date" />
        <F k="payout_bank" label="Payout bank" />
        <F k="bank_account" label="Bank account no." />
        <F k="bank_ifsc" label="IFSC" />
        <F k="upi" label="UPI ID" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold">Documents (ID / PAN / contract etc.)</Label>
        <div className="flex flex-wrap gap-3">
          {(p.documents || []).map((d, i) => (<a key={i} href={img(d.path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline flex items-center gap-1" data-testid="profile-doc"><FileArrowDown size={13} /> {d.name || "Document"}</a>))}
          {(p.documents || []).length === 0 && <span className="text-xs text-muted-foreground">No documents uploaded.</span>}
        </div>
        {manager && <><input ref={docRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e, (d) => save({ documents: [...(p.documents || []), { name: d.filename, path: d.path }] }))} data-testid="profile-doc-input" /><Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => docRef.current?.click()} data-testid="profile-doc-btn"><Plus size={13} className="mr-1" /> Upload document</Button></>}
      </div>
    </Card>
  );
}
